// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import ChangeScheduleModal from "@eisinc/App/Components/Cart/ChangeScheduleModal";
import { parseBoolean } from "@eisinc/App/Utilities/helpers";
import AddressInfoDisplay, { AddressInfoDisplayStyles } from "@eisinc/Overrides/Components/AddressInfoDisplay";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setStorePickupOverlayIsOpen from "@insite/client-framework/Store/Components/StorePickupOverlay/Handlers/SetStorePickupOverlayIsOpen";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import setFulfillmentMethod from "@insite/client-framework/Store/Context/Handlers/SetFulfillmentMethod";
import updatePickUpWarehouse from "@insite/client-framework/Store/Context/Handlers/UpdatePickUpWarehouse";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import setFulfillmentMethodForCart from "@insite/client-framework/Store/Data/Carts/Handlers/SetFulfillmentMethod";
import updatePickUpWarehouseForCart from "@insite/client-framework/Store/Data/Carts/Handlers/UpdatePickUpWarehouse";
import translate from "@insite/client-framework/Translate";
import { WarehouseModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import FindLocationModal, { FindLocationModalStyles } from "@insite/content-library/Components/FindLocationModal";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal from "@insite/mobius/Modal";
import Radio, { RadioComponentProps } from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupComponentProps } from "@insite/mobius/RadioGroup";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import FieldSetPresentationProps, { FieldSetGroupPresentationProps } from "@insite/mobius/utilities/fieldSetProps";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import React, { useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

const enum fields {
    displayStyle = "displayStyle",
    shipToHomeMessage = "shipToHomeMessage",
    storePickUpMessage = "storePickUpMessage",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.displayStyle]: string;
        [fields.shipToHomeMessage]: string;
        [fields.storePickUpMessage]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const cart = getCurrentCartState(state).value;
    const session = getSession(state);
    return {
        fulfillmentMethod: cart?.fulfillmentMethod || session.fulfillmentMethod,
        enableWarehousePickup: getSettingsCollection(state).accountSettings.enableWarehousePickup,
        pickUpWarehouse: cart?.defaultWarehouse || session.pickUpWarehouse,
        updateCartInsteadOfContext: cart?.isAwaitingApproval,
        cart,
    };
};

const mapDispatchToProps = {
    setFulfillmentMethod,
    updatePickUpWarehouse: makeHandlerChainAwaitable(updatePickUpWarehouse),
    setStorePickupOverlayIsOpen,
    setFulfillmentMethodForCart,
    updatePickUpWarehouseForCart: makeHandlerChainAwaitable(updatePickUpWarehouseForCart),
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    ThemeProps<BaseTheme>;

export interface CartFulfillmentMethodSelectorStyles {
    fulfillmentMethodradioGroup?: FieldSetGroupPresentationProps<RadioGroupComponentProps>;
    fulfillmentMethodRadio?: FieldSetPresentationProps<RadioComponentProps>;
    pickUpLocation?: PickUpLocationStyles;
    clickableBoxesText?: TypographyPresentationProps;
    boxesWrapper?: InjectableCss;
    boxWrapper?: InjectableCss;
    selectedBoxWrapper?: InjectableCss;
    clickableBoxLabel?: TypographyPresentationProps;
    pickUpWarehouseLink?: LinkPresentationProps;
    shipToHomeMessage?: TypographyPresentationProps;
    storePickUpMessage?: TypographyPresentationProps;
    findLocationModal?: FindLocationModalStyles;
    // XNG-Changes
    fulfillmentMethodWrapper?: InjectableCss;
}

interface PickUpLocationStyles {
    wrapper?: InjectableCss;
    headerText?: TypographyProps;
    changeLink?: LinkPresentationProps;
    warehouseAddress?: WarehouseAddressInfoDisplayStyles;
    // XNG-Changes
    pickupAddressWrapper?: InjectableCss;
    directionsLink?: LinkPresentationProps;
    instructionsLink?: LinkPresentationProps;
}

interface WarehouseAddressInfoDisplayStyles {
    wrapper?: InjectableCss;
    nameText?: TypographyProps;
    address?: AddressInfoDisplayStyles;
}

export const cartFulfillmentMethodStyles: CartFulfillmentMethodSelectorStyles = {
    pickUpLocation: {
        wrapper: {
            css: css`
                margin-top: 20px;
            `,
        },
        headerText: {
            weight: 600,
        },
        changeLink: {
            css: css`
                margin-left: 15px;
            `,
        },
        warehouseAddress: {
            wrapper: {
                css: css`
                    margin-top: 8px;
                `,
            },
            nameText: {
                as: "p",
            },
        },
    },
    boxesWrapper: {
        css: css`
            display: flex;
            gap: 8px;
        `,
    },
    boxWrapper: {
        css: css`
            border: 1px solid ${getColor("common.border")};
            padding: 15px;
            margin-top: 15px;
            display: flex;
            flex-direction: column;
            width: 50%;
            align-items: flex-start;
            cursor: pointer;
        `,
    },
    selectedBoxWrapper: {
        css: css`
            border: 2px solid ${getColor("secondary.main")};
            padding: 15px;
            margin-top: 15px;
            display: flex;
            flex-direction: column;
            width: 50%;
            align-items: flex-start;
            cursor: pointer;
        `,
    },
    clickableBoxLabel: {
        css: css`
            margin-bottom: 8px;
        `,
    },
};

const styles = cartFulfillmentMethodStyles;

const CartFulfillmentMethodSelector = ({
    fulfillmentMethod,
    enableWarehousePickup,
    pickUpWarehouse,
    fields,
    setFulfillmentMethod,
    updatePickUpWarehouse,
    setStorePickupOverlayIsOpen,
    theme,
    updateCartInsteadOfContext,
    setFulfillmentMethodForCart,
    updatePickUpWarehouseForCart,
    cart,
}: Props) => {
    const [isFindLocationOpen, setIsFindLocationOpen] = useState(false);
    const toasterContext = useContext(ToasterContext);

    // XNG-Change
    const dispatch = useAppDispatch();

    // XNG-Changes add !cart.cartLines?.length to the condition
    if (!fulfillmentMethod || !enableWarehousePickup || !cart?.cartLines?.length) {
        return null;
    }

    const { shipToHomeMessage, storePickUpMessage } = fields;
    const displayStyle = fields.displayStyle || "radioButton";
    const fulfillmentMethodChangeHandler = (fulfillmentMethod: string) => {
        // XNG-Change:  display confirmation modal if cart has Scheduled Deliveries
        if (parseBoolean(cart.properties["hasScheduledDeliveries"])) {
            dispatch({ type: "xCommon/update", data: { key: "isChangeScheduleModalOpen", value: true } });
            return;
        }
        if (updateCartInsteadOfContext && cart) {
            setFulfillmentMethodForCart({
                cart,
                fulfillmentMethod: fulfillmentMethod as "Ship" | "PickUp",
            });
        } else {
            setFulfillmentMethod({ fulfillmentMethod: fulfillmentMethod as "Ship" | "PickUp" });
        }
    };

    // XNG-Change
    const handleContinue = () => {
        if (updateCartInsteadOfContext && cart) {
            setFulfillmentMethodForCart({
                cart,
                fulfillmentMethod: FulfillmentMethod.PickUp,
            });
        } else {
            setFulfillmentMethod({ fulfillmentMethod: FulfillmentMethod.PickUp });
        }
        dispatch({ type: "xCommon/update", data: { key: "isChangeScheduleModalOpen", value: false } });
    };

    const handlePickUpAddressChange = async (pickUpWarehouse: WarehouseModel) => {
        await updatePickUpWarehouse({ pickUpWarehouse });
        if (updateCartInsteadOfContext && cart) {
            await updatePickUpWarehouseForCart({ cart, pickUpWarehouse });
        } else {
            await updatePickUpWarehouse({ pickUpWarehouse });
        }

        toasterContext.addToast({
            body: translate("Pick Up Address Updated"),
            messageType: "success",
        });
    };

    const handleOpenFindLocation = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        event.stopPropagation();

        if (theme.storePickup?.useOverlay) {
            setStorePickupOverlayIsOpen({ isOpen: true });
            return;
        }

        setIsFindLocationOpen(true);
    };
    const handleFindLocationModalClose = () => setIsFindLocationOpen(false);
    const handleWarehouseSelected = (warehouse: WarehouseModel) => {
        handlePickUpAddressChange(warehouse);
        setIsFindLocationOpen(false);
    };

    // Xng-change: EIS-3697: adding a check when checkout is disabled for quickFab products
    const isPickupMethodDisabled = cart.properties.disablePickupMethod === "true";

    return (
        // xng-change: adding wrapper section for the fulfillment method selector
        // and aria-labelledby because we have multiple sections in the same page
        // not aria-label, because we have have a another label inside
        <StyledWrapper
            as="section"
            aria-labelledby={translate("Fulfillment Method")}
            className={fulfillmentMethod === FulfillmentMethod.Ship ? "half-width" : "full-width"}
            {...styles.fulfillmentMethodWrapper}
        >
            {displayStyle === "radioButton" && (
                <>
                    <RadioGroup
                        label={translate("Fulfillment Method")}
                        value={fulfillmentMethod}
                        onChangeHandler={event => fulfillmentMethodChangeHandler(event.currentTarget.value)}
                        {...styles.fulfillmentMethodradioGroup}
                        data-test-selector="cartFulfillmentMethod"
                    >
                        <Radio
                            value={FulfillmentMethod.Ship}
                            {...styles.fulfillmentMethodRadio}
                            data-test-selector="fulfillmentMethod_ship"
                        >
                            {translate("Delivery")}
                        </Radio>
                        <Radio
                            value={FulfillmentMethod.PickUp}
                            {...styles.fulfillmentMethodRadio}
                            data-test-selector="fulfillmentMethod_pickUp"
                            // XNG-Changes : disable "Pick Up" when isPickupMethodDisabled
                            disabled={isPickupMethodDisabled}
                        >
                            {translate("Pick Up")}
                        </Radio>
                    </RadioGroup>
                    {fulfillmentMethod === FulfillmentMethod.PickUp && pickUpWarehouse && (
                        <PickUpLocation warehouse={pickUpWarehouse} openFindLocation={handleOpenFindLocation} />
                    )}
                </>
            )}
            {displayStyle === "clickableBoxes" && (
                <>
                    <Typography {...styles.clickableBoxesText}>
                        {siteMessage("Cart_FulfillmentMethodMessage")}
                    </Typography>
                    <StyledWrapper {...styles.boxesWrapper}>
                        <StyledWrapper
                            {...(fulfillmentMethod === FulfillmentMethod.Ship
                                ? styles.selectedBoxWrapper
                                : styles.boxWrapper)}
                            onClick={() => fulfillmentMethodChangeHandler(FulfillmentMethod.Ship)}
                        >
                            <Typography {...styles.clickableBoxLabel}>{translate("Ship to home")}</Typography>
                            <Typography {...styles.shipToHomeMessage}>
                                {parse(shipToHomeMessage || "", parserOptions)}
                            </Typography>
                        </StyledWrapper>
                        <StyledWrapper
                            {...(fulfillmentMethod === FulfillmentMethod.PickUp
                                ? styles.selectedBoxWrapper
                                : styles.boxWrapper)}
                            onClick={() => fulfillmentMethodChangeHandler(FulfillmentMethod.PickUp)}
                        >
                            <Typography {...styles.clickableBoxLabel}>
                                {pickUpWarehouse ? translate("Store pickup at") : translate("Store pickup")}
                                {pickUpWarehouse && (
                                    <Link onClick={handleOpenFindLocation} {...styles.pickUpWarehouseLink}>
                                        &nbsp;{pickUpWarehouse.description || pickUpWarehouse.name}
                                    </Link>
                                )}
                            </Typography>
                            <Typography {...styles.storePickUpMessage}>
                                {parse(storePickUpMessage || "", parserOptions)}
                            </Typography>
                        </StyledWrapper>
                    </StyledWrapper>
                </>
            )}
            <FindLocationModal
                modalIsOpen={isFindLocationOpen}
                onWarehouseSelected={handleWarehouseSelected}
                onModalClose={handleFindLocationModalClose}
                forCart
                extendedStyles={styles.findLocationModal}
            />
            {/* XNG-Change */}
            <ChangeScheduleModal handleContinue={handleContinue} />
        </StyledWrapper>
    );
};

interface PickUpLocationProps {
    warehouse: WarehouseModel;
    openFindLocation: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
}

const PickUpLocation = ({ warehouse, openFindLocation }: PickUpLocationProps) => {
    const componentStyles = styles.pickUpLocation || {};
    const [warehouseHoursToDisplay, setWarehouseHoursToDisplay] = React.useState<WarehouseModel | undefined>(undefined);
    const [warehouseHoursToDisplayModalOpen, setWarehouseHoursToDisplayModalOpen] = React.useState<boolean>(false);

    const handleOpenHoursModal = (warehouse: WarehouseModel) => {
        setWarehouseHoursToDisplayModalOpen(!!warehouse.hours);
        setWarehouseHoursToDisplay(warehouse);
    };

    const handleHoursModalClose = () => {
        setWarehouseHoursToDisplayModalOpen(false);
        setWarehouseHoursToDisplay(undefined);
    };

    const linkAddress = `http://maps.google.com/maps?daddr=${warehouse.address1} ${warehouse.address2}, ${warehouse.city}, ${warehouse.state} ${warehouse.postalCode}`;

    return (
        <StyledWrapper {...componentStyles.pickupAddressWrapper}>
            <StyledWrapper {...componentStyles.wrapper} data-test-selector="cart_pickUpLocation">
                <WarehouseAddressInfoDisplay warehouse={warehouse} />
            </StyledWrapper>
            <Link
                onClick={openFindLocation}
                {...componentStyles.changeLink}
                data-test-selector="cart_pickUpLocation_findLocation"
            >
                {translate("Change Pick up Location")}
            </Link>
            <Link
                href={linkAddress}
                {...componentStyles.directionsLink}
                target="_blank"
                data-test-selector="cart_pickUpLocation_directions"
            >
                {translate("Get Directions")}
            </Link>
            {warehouse && warehouse.hours && (
                <Link
                    {...componentStyles.instructionsLink}
                    onClick={() => handleOpenHoursModal(warehouse)}
                    data-test-selector="cart_pickUpLocation_instructions"
                >
                    {translate("Pick up Instructions")}
                </Link>
            )}
            <Modal
                headline={translate("Hours")}
                isOpen={warehouseHoursToDisplayModalOpen}
                handleClose={handleHoursModalClose}
            >
                <StyledWrapper>{parse(warehouseHoursToDisplay?.hours || "", parserOptions)}</StyledWrapper>
            </Modal>
        </StyledWrapper>
    );
};

interface WarehouseAddressInfoDisplayProps {
    warehouse: WarehouseModel;
}

const WarehouseAddressInfoDisplay = ({ warehouse }: WarehouseAddressInfoDisplayProps) => {
    const componentStyles =
        styles.pickUpLocation && styles.pickUpLocation.warehouseAddress ? styles.pickUpLocation.warehouseAddress : {};

    return (
        <StyledWrapper {...componentStyles.wrapper}>
            <Typography {...componentStyles.nameText} data-test-selector="cart_warehouseName">
                {warehouse.description || warehouse.name}
            </Typography>
            <AddressInfoDisplay
                address1={warehouse.address1}
                address2={warehouse.address2}
                city={warehouse.city}
                state={warehouse.state}
                postalCode={warehouse.postalCode}
                phone={warehouse.phone}
                extendedStyles={componentStyles.address}
            />
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withTheme(CartFulfillmentMethodSelector)),
    definition: {
        group: "Cart",
        displayName: "Fulfillment Method Selector",
        icon: "truck",
        allowedContexts: ["CartPage"],
        fieldDefinitions: [
            {
                name: fields.displayStyle,
                displayName: "Display Style",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Radio Button",
                        value: "radioButton",
                    },
                    {
                        displayName: "Clickable Boxes",
                        value: "clickableBoxes",
                    },
                ],
                defaultValue: "radioButton",
                fieldType: "General",
            },
            {
                name: fields.shipToHomeMessage,
                displayName: "Ship to home message",
                defaultValue: "",
                editorTemplate: "RichTextField",
                fieldType: "Contextual",
                extendedConfig: { height: 100 },
                isVisible: item => item?.fields[fields.displayStyle] === "clickableBoxes",
            },
            {
                name: fields.storePickUpMessage,
                displayName: "Store PickUp message",
                defaultValue: "",
                editorTemplate: "RichTextField",
                fieldType: "Contextual",
                extendedConfig: { height: 100 },
                isVisible: item => item?.fields[fields.displayStyle] === "clickableBoxes",
            },
        ],
    },
};

export default widgetModule;
